/**
 * @class
 * @description This element ensures the bundle requirement id input is kept up-to-date when the cart state changes
 *
 * @extends HTMLElement
 */
class BundleProperties extends window.BAO.CustomElement() {
  static busy = false
  static cartState = null
  static configurations = null
  static claimedBundleIds = null
  static claimedRequirementIds = null
  static hasCartChangedHandler = false

  /**
   * @description The names of internal elements required by this custom element.
   *
   * @returns {string[]}
   */
  static get requiredElements () {
    return ['satisfied-requirement-ids', 'bundle-uuid']
  }

  /**
   * @description The names of external elements required by this custom element.
   *
   * @returns {string[]}
   */
  static get requiredExternalElements () {
    return ['configurations']
  }

  /**
   * @description The names of attributes required by this custom element.
   *
   * @returns {string[]}
   */
  static get requiredAttributes () {
    return ['data-satisfied-requirement-ids']
  }

  /**
   * @function
   * @description Called each time the element is added to the document.
   *
   * @returns {void}
   */
  async connectedCallback () {
    super.connectedCallback()
    this.setupEls()

    // Cache configurations against the instance of the class
    if (!BundleProperties.configurations) {
      this.setConfigurations()
    }

    // Cache cart state against the instance of the class
    // Prevents multiple requests for cart state
    // State is communicated to all custom elements via Custom Event
    if (!BundleProperties.cartState) {
      this.fetchCartState()
    }
  }

  /**
   * @function
   * @description Called each time the element is removed from the document.
   *
   * @returns {void}
   */
  disconnectedCallback () {
    super.disconnectedCallback()

    if (BundleProperties.hasCartChangedHandler) {
      BundleProperties.hasCartChangedHandler = false

      window.BAO.eventBus.removeEventListener(
        window.BAO.EVENTS.PUSH_CART.CHANGED,
        this.fetchCartState.bind(this),
      )
    }

    window.BAO.eventBus.removeEventListener(
      this.eventName,
      this.handleCartStateUpdated.bind(this),
    )
  }

  /**
   * @function
   * @description Sets up the elements required by this class.
   *
   * @returns {void}
   */
  setupEls () {
    this.configurationsEl = this.externalEls?.configurations?.element
    this.requirementIdsInputEl = this.els?.[
      'satisfied-requirement-ids'
    ]?.element
    this.uuidInputEl = this.els?.['bundle-uuid']?.element
  }

  /**
   * @function
   * @description Sets up the required event handlers.
   *
   * @returns {void}
   */
  setupListeners () {
    super.setupListeners()

    if (!BundleProperties.hasCartChangedHandler) {
      BundleProperties.hasCartChangedHandler = true

      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.PUSH_CART.CHANGED,
        this.fetchCartState.bind(this),
      )
    }

    window.BAO.eventBus.addEventListener(
      this.eventName,
      this.handleCartStateUpdated.bind(this),
    )
  }

  /**
   * @function
   * @description Handles cart state changes.
   *
   * @returns {void}
   */
  handleCartStateUpdated () {
    this.resetSatisfiedRequirementIdsInput()
    this.updateSatisfiedRequirementIdsInput()
    this.updateBundleUUID()
  }

  /**
   * @function
   * @description Resets the value of the input to the default value
   *
   * @returns {void}
   */
  resetSatisfiedRequirementIdsInput () {
    if (!this.requirementIdsInputEl) return

    this.requirementIdsInputEl.value = this.dataset.satisfiedRequirementIds
  }

  /**
   * @function
   * @description Updates the value of the input based on the state of the cart.
   * Requirement ids from claimed bundles are removed.
   *
   * @returns {void}
   */
  updateSatisfiedRequirementIdsInput () {
    if (
      !this.requirementIdsInputEl ||
      !BundleProperties.claimedRequirementIds.length
    ) {
      return
    }

    const satisfiedRequirementIds =
      this.requirementIdsInputEl.value
        ?.split(':')
        ?.filter(value => value.length) ?? []
    const filteredRequirementIds = satisfiedRequirementIds.filter(
      satisfiedRequirementId =>
        !BundleProperties.claimedRequirementIds.includes(satisfiedRequirementId),
    )
    this.requirementIdsInputEl.value = filteredRequirementIds.join(':')
  }

  /**
   * @function
   * @description Updates the value of the uuid input based on the state of the cart.
   * Value gets updated every time the number of bundles in the cart changes
   *
   * @returns {void}
   */
  updateBundleUUID () {
    if (!this.uuidInputEl || !this.storage.uuid) return

    this.uuidInputEl.value = this.storage.uuid
  }

  /**
   * @description Get a configuration by it's associated bundle id.
   * @param {Configuration["bundle_id"]} bundleId A bundle id.
   *
   * @returns {Configuration}
   */
  getConfigurationByBundleId (bundleId) {
    return BundleProperties.configurations.find(
      configuration => configuration.bundle_id === bundleId,
    )
  }

  /**
   * @description Get all requirement ids associated with a given configuration.
   * @param {Configuration} configuration A configuration object.
   *
   * @returns {Configuration["requirements"][number]["requirement_id"][]}
   */
  getRequirementIdsFromConfiguration (configuration) {
    return configuration.requirements.map(
      requirement => requirement.requirement_id,
    )
  }

  /**
   * @description Fetches the current cart state and caches it for use by all elements.
   *
   * @returns {void}
   */
  async fetchCartState () {
    if (BundleProperties.busy) return
    BundleProperties.busy = true

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.json`)
      const cartState = await response.json()

      this.setClaimedBundleIds(cartState)
      this.setClaimedRequirementIds(cartState)
      this.updateStorage()

      BundleProperties.cartState = cartState
      BundleProperties.busy = false
      window.BAO.dispatchEvent(this.eventName)
    } catch (error) {
      console.error(error)

      BundleProperties.cartState = {}
    }
  }

  /**
   * @description Caches the configurations for use by all elements.
   *
   * @returns {void}
   */
  setConfigurations () {
    BundleProperties.configurations = JSON.parse(
      this.configurationsEl?.innerHTML ?? '[]',
    )
  }

  /**
   * @description Caches the claimed bundle ids for use by all elements.
   *
   * @param {CartState} cartState The current cart state.
   * @returns {void}
   */
  setClaimedBundleIds (cartState) {
    BundleProperties.claimedBundleIds = cartState.items
      .map(lineItem => {
        return lineItem.properties?.[this.bundleIdPropertyKey]
      })
      .filter(Boolean)
  }

  /**
   * @description Caches the claimed requirement ids for use by all elements.
   *
   * @param {CartState} cartState The current cart state.
   * @returns {void}
   */
  setClaimedRequirementIds (cartState) {
    const bundleLineItems = cartState.items.filter(
      lineItem => lineItem.properties?.[this.bundleIdPropertyKey]?.length,
    )
    const claimedBundleIds = bundleLineItems.map(
      lineItem => lineItem.properties?.[this.bundleIdPropertyKey],
    )

    BundleProperties.claimedRequirementIds = claimedBundleIds.flatMap(
      claimedBundleId => {
        const configuration = this.getConfigurationByBundleId(claimedBundleId)
        return this.getRequirementIdsFromConfiguration(configuration)
      },
    )
  }

  /**
   * @description Updates the stored bundle count and uuid.
   *
   * @returns {void}
   */
  updateStorage () {
    const currentBundleCount = BundleProperties.claimedBundleIds.length

    if (this.storage?.count === currentBundleCount) return

    this.storage = {
      count: currentBundleCount,
      uuid: crypto.randomUUID(),
    }
  }

  /**
   * @description An object containing the bundle count and a unique uuid.
   *
   * @returns {BundleStorage}
   */
  get storage () {
    return JSON.parse(window.localStorage.getItem(this.storageKey) || '{}')
  }

  /**
   * @description Stores the bundle count and a unique uuid.
   *
   * @param {BundleStorage} value An object containing the current bundle count and uuid.
   * @returns {void}
   */
  set storage (value) {
    window.BAO.Cart.updateCartAttributes({
      _bundle_uuid: value.uuid,
    })

    window.localStorage.setItem(this.storageKey, JSON.stringify(value))
  }

  /**
   * @description The key of the line item property used to store id of the claimed bundle.
   *
   * @returns {string}
   */
  get bundleIdPropertyKey () {
    return '_bundle_id'
  }

  /**
   * @description The name of the custom event to fire after the cart state has been updated.
   *
   * @returns {string}
   */
  get eventName () {
    return 'BUNDLE_PROPERTIES_CART_STATE_UPDATED'
  }

  /**
   * @description The key used to store data against in local storage.
   *
   * @returns {string}
   */
  get storageKey () {
    return 'bundle-properties'
  }
}

if (!window.customElements.get('bundle-properties')) {
  window.customElements.define('bundle-properties', BundleProperties)
}

/**
 * @typedef {object} Requirement
 * @property {string} requirement_id The id of this requirement.
 */

/**
 * @typedef {object} Configuration
 * @property {string} bundle_id The id of the bundle associated with this configurations.
 * @property {Requirement[]} requirements An array of requirements associated with this configuration.
 */

/**
 * @typedef {object} BundleStorage
 * @property {number} count The current number of bundles in the cart.
 * @property {string} uuid A unique identifier.
 */

/**
 * @typedef {object} CartState
 * @property {string} token
 * @property {string} note
 * @property {CartAttributes} attributes
 * @property {number} original_total_price
 * @property {number} total_price
 * @property {number} total_discount
 * @property {number} total_weight
 * @property {number} item_count
 * @property {CartItem[]} items
 * @property {boolean} requires_shipping
 * @property {string} currency
 * @property {number} items_subtotal_price
 * @property {[]} cart_level_discount_applications
 */

/**
 * @typedef {object} CartAttributes
 * @property {string} _bundle_uuid
 */

/**
 * @typedef {object} CartItem
 * @property {number} id
 * @property {CartItemProperties} properties
 * @property {number} quantity
 * @property {number} variant_id
 * @property {string} key
 * @property {string} title
 * @property {number} price
 * @property {number} original_price
 * @property {number} presentment_price
 * @property {number} discounted_price
 * @property {number} line_price
 * @property {number} original_line_price
 * @property {number} total_discount
 * @property {[]} discounts
 * @property {string} sku
 * @property {number} grams
 * @property {string} vendor
 * @property {boolean} taxable
 * @property {number} product_id
 * @property {boolean} product_has_only_default_variant
 * @property {boolean} gift_card
 * @property {number} final_price
 * @property {number} final_line_price
 * @property {string} url
 * @property {FeaturedImage} featured_image
 * @property {string} image
 * @property {string} handle
 * @property {boolean} requires_shipping
 * @property {string} product_type
 * @property {string} product_title
 * @property {string} product_description
 * @property {string} variant_title
 * @property {string[]} variant_options
 * @property {OptionsWithValues[]} options_with_values
 * @property {[]} line_level_discount_allocations
 * @property {number} line_level_total_discount
 * @property {QuantityRule} quantity_rule
 * @property {boolean} has_components
 */

/**
 * @typedef {object} CartItemProperties
 * @property {string} _bundle_id
 * @property {string} _bundle_uuid
 */

/**
 * @typedef {object} FeaturedImage
 * @property {number} aspect_ratio
 * @property {string} alt
 * @property {number} height
 * @property {string} url
 * @property {number} width
 */

/**
 * @typedef {object} OptionsWithValues
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {object} QuantityRule
 * @property {number} min
 * @property {number | null} max
 * @property {number} increment
 */
